import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { NotificationsService, QueryNotificationsDto } from '../services/notifications.service';
import { useAuthStore } from '../store/auth.store';

// Every notification query starts with this key, so one invalidation refreshes the bell, the
// dropdown and the page together
export const NOTIFICATIONS_KEY = ['notifications'];

export const useNotificationStats = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'stats'],
    queryFn: () => NotificationsService.getStats(),
    enabled: isAuthenticated,
    // New notifications are pushed over the socket; this covers the case where that is not connected
    refetchInterval: 60_000,
  });
};

export const useNotificationsPage = (params: QueryNotificationsDto) => {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'page', params],
    queryFn: () => NotificationsService.getNotifications(params),
    placeholderData: keepPreviousData,
  });
};

// The latest few, for the dropdown under the bell
export const useRecentNotifications = (enabled: boolean) => {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'recent'],
    queryFn: () => NotificationsService.getNotifications({ limit: 8 }),
    enabled,
  });
};

const useInvalidateNotifications = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
};

export const useMarkNotification = () => {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      read ? NotificationsService.markAsRead(id) : NotificationsService.markAsUnread(id),
    onSuccess: invalidate,
  });
};

export const useMarkAllNotificationsRead = () => {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: () => NotificationsService.markAllAsRead(),
    onSuccess: invalidate,
  });
};

export const useBulkNotifications = () => {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: ({ ids, action }: { ids: string[]; action: 'read' | 'unread' | 'delete' }) =>
      NotificationsService.bulk(ids, action),
    onSuccess: invalidate,
  });
};

export const useDeleteNotification = () => {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (id: string) => NotificationsService.deleteNotification(id),
    onSuccess: invalidate,
  });
};

export const useDeleteNotifications = () => {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (scope: 'read' | 'all') => NotificationsService.deleteAllNotifications(scope),
    onSuccess: invalidate,
  });
};

export const useScanForAlerts = () => {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: () => NotificationsService.scan(),
    onSuccess: invalidate,
  });
};
